import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, Link, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { OverlayProvider } from "./hooks/useOverlay";
import { CookiesProvider } from "react-cookie";
import { Toaster } from "react-hot-toast";
import Icon from "@mdi/react";
import { mdiCheckCircle } from "@mdi/js";
import { GLOBAL_ERROR_TOAST_ID, GLOBAL_SUCCESS_TOAST_ID } from "@/shared/constants/settings";
import { Spinner } from "@/components/common/Spinner";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      retry: false
    }
  }
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: {
    queryClient
  },
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: () => {
    return (
      <div className="flex flex-col items-center justify-center w-screen h-screen gap-4">
        <img src="/assets/NotFound.png" alt="" width={240} height={304} />
        <div className="flex flex-col gap-2">
          <p className="text-2xl font-medium leading-[150%] tracking-[-0.48px] whitespace-pre-wrap md:whitespace-normal text-center">
            Sorry, the page cannot be found
          </p>
          <p className="text-base font-normal leading-[150%] tracking-[-0.32px] whitespace-pre-wrap md:whitespace-normal text-center">
            We looked everywhere but couldn’t find the\n page you’re looking for.
          </p>
        </div>
        <Link to="/">
          <a className="px-4 py-2 bg-white border-black rounded border-[1px] border-[rgba(27, 8, 45, 0.50)] border-solid">
            Go back home
          </a>
        </Link>
      </div>
    );
  },
  defaultErrorComponent: ({ error }) => {
    useEffect(() => {
      Sentry.withScope(scope => {
        scope.setLevel("error");
        Sentry.captureException(error);
      });
    }, [error]);

    return (
      <div className="flex flex-col items-center justify-center w-screen h-screen">
        <img src="/assets/error.png" alt="" width={326} height={304} />
        <h1 className="mt-4 text-2xl leading-normal tracking-[-0.48px] font-medium">Temporary issue has occurred</h1>
        <p className="mt-2 text-primaryGray-300 leading-normal tracking-[-0.32px] text-center">
          If the problem persists, please contact us at info@buildblock.ai
        </p>
      </div>
    );
  }
});

function App() {
  useEffect(() => {
    const handleResize = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CookiesProvider defaultSetOptions={{ path: "/" }}>
        <Toaster
          toastOptions={{
            duration: 3000,
            position: "top-center",
            success: {
              id: GLOBAL_SUCCESS_TOAST_ID,
              icon: <Icon path={mdiCheckCircle} size="1.5rem" className="text-maruGreen" />
            },
            error: {
              id: GLOBAL_ERROR_TOAST_ID
            },
            loading: {
              icon: <Spinner size={24} />
            }
          }}
        />
        <HelmetProvider>
          <RouterProvider router={router} InnerWrap={({ children }) => <OverlayProvider>{children}</OverlayProvider>} />
        </HelmetProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </CookiesProvider>
    </QueryClientProvider>
  );
}

export default App;
